/**
 * Create OR update form logic — field shape is driven by the
 * IntegrationDefinition passed in (from the server's catalog), not by any
 * hardcoded client-side map. Decides between POST (connect) and PUT
 * (rotate fields on existing row) based on the `mode` prop.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type { IntegrationDefinition, TenantIntegrationSummary } from '../../lib/types.js';

export type IntegrationFormMode = 'create' | 'edit';

interface IntegrationFormLogic {
  fields: Record<string, string>;
  setField: (name: string, value: string) => void;
  isSubmitting: boolean;
  error: Error | null;
  submit: () => Promise<void>;
}

/**
 * In edit mode we only send fields the user actually changed (or filled
 * in). Empty secret fields stay empty so the server's partial-update
 * semantics keep the existing KV value. In create mode we send everything
 * — secrets must be entered, literals fall back to defaultValue.
 */
const buildSubmitPayload = (
  definition: IntegrationDefinition,
  fields: Record<string, string>,
  mode: IntegrationFormMode,
): Record<string, string> => {
  const payload: Record<string, string> = {};
  for (const field of definition.fields) {
    const value = fields[field.name] ?? '';
    if (mode === 'edit') {
      if (value !== '') payload[field.name] = value;
    } else {
      if (value !== '') {
        payload[field.name] = value;
      } else if (field.defaultValue !== undefined) {
        payload[field.name] = field.defaultValue;
      }
    }
  }
  return payload;
};

/**
 * Build the form's starting state.
 *
 * - **Create mode**: every field starts at its catalog `defaultValue`
 *   (or empty if there is none). User fills in everything that's empty.
 * - **Edit mode**: non-secret fields prefill from `prefill` (the stored
 *   values returned by the server). Secret fields stay empty so the
 *   existing "empty == don't rotate this" semantics still apply.
 */
const initialFields = (
  definition: IntegrationDefinition,
  mode: IntegrationFormMode,
  prefill: Record<string, string>,
): Record<string, string> => {
  const init: Record<string, string> = {};
  for (const field of definition.fields) {
    if (field.kind === 'secret') {
      // Never prefilled: the stored value is a Key Vault reference, and an empty
      // box means "leave it as it is".
      init[field.name] = '';
      continue;
    }
    // Stored value, else the catalog default — in BOTH modes. Edit used to fall
    // back to '' instead, which for a select rendered a value matching no option:
    // the control looked blank, its conditional children stayed hidden (because ''
    // never equals their visibleWhen), and buildSubmitPayload skips empty values so
    // it was never submitted. A connection configured before a field existed in the
    // catalog could therefore never be given one through the form.
    init[field.name] = prefill[field.name] ?? field.defaultValue ?? '';
  }
  return init;
};

export const useIntegrationFormLogic = (
  tenantId: string | undefined,
  definition: IntegrationDefinition,
  mode: IntegrationFormMode,
  onSuccess: () => void,
  /**
   * Stored non-secret values for the integration (Edit mode only).
   * Comes from `TenantIntegrationSummary.config`. Empty record in
   * Create mode.
   */
  prefill: Record<string, string> = {},
  /**
   * Edit mode only: the row being updated. PUT addresses a configured row by id,
   * because a catalog key can have several instances and so can't name one.
   */
  integrationId?: string,
  /**
   * Create mode only: name + slug for a SECOND instance of a key. Omitted, the
   * server defaults the slug to the key — the single-instance case.
   */
  instance?: { name?: string; slug?: string },
  /**
   * Edit mode only: a new display name for the instance. Sent on the PUT so a
   * connection can be renamed; the slug is never touched.
   */
  renameTo?: string,
): IntegrationFormLogic => {
  const queryClient = useQueryClient();
  const [fields, setFields] = useState<Record<string, string>>(() =>
    initialFields(definition, mode, prefill),
  );

  // If the definition changes (user opens form for a different integration),
  // reset the field state. Keyed on `definition.key` rather than the whole
  // object so identity changes that aren't real key changes don't reset.
  // `prefill` is intentionally NOT in the dep array — it would re-init the
  // form on every parent render (the object identity changes even when
  // values don't). definition.key change is the right re-init trigger.
  useEffect(() => {
    setFields(initialFields(definition, mode, prefill));
  }, [definition, mode]);

  const mutation = useMutation<TenantIntegrationSummary, Error, void>({
    mutationFn: () => {
      const payload = buildSubmitPayload(definition, fields, mode);
      if (mode === 'create') {
        return apiRequest<TenantIntegrationSummary>(`/api/integrations`, {
          method: 'POST',
          body: {
            key: definition.key,
            fields: payload,
            ...(instance?.name ? { name: instance.name } : {}),
            ...(instance?.slug ? { slug: instance.slug } : {}),
          },
        });
      }
      return apiRequest<TenantIntegrationSummary>(`/api/integrations/${integrationId}`, {
        method: 'PUT',
        body: { fields: payload, ...(renameTo ? { name: renameTo } : {}) },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.integrations(tenantId ?? '') });
      // Configuring a source changes what the capability page shows (a source
      // becomes "configured"), so refresh capability state too.
      void queryClient.invalidateQueries({ queryKey: queryKeys.capabilities(tenantId ?? '') });
      onSuccess();
    },
  });

  return {
    fields,
    setField: (name, value) => setFields((prev) => ({ ...prev, [name]: value })),
    isSubmitting: mutation.isPending,
    error: mutation.error,
    submit: async () => {
      await mutation.mutateAsync();
    },
  };
};
